import { useEffect, useState } from 'react'
import { GlobalOutlined, BulbOutlined, LoadingOutlined, SearchOutlined } from '@ant-design/icons'
import type { HarnessRoundLog } from '../api/chatStream'

export type WebRef = {
  title?: string
  url?: string
  snippet?: string
  cite_index?: number
}

export type StreamPhase = 'retrieval' | 'curation' | 'thinking' | 'judging' | 'answering' | 'done'

type ChatThinkingProps = {
  content: string
  streaming?: boolean
  phase?: StreamPhase
}

export function ChatThinking({ content, streaming = false, phase = 'done' }: ChatThinkingProps) {
  const [open, setOpen] = useState(false)
  const showPanel = streaming || !!content.trim()

  useEffect(() => {
    if (streaming && phase === 'thinking') {
      setOpen(true)
      return
    }
    if (phase === 'answering' || phase === 'done') {
      setOpen(false)
    }
  }, [streaming, phase])

  if (!showPanel) return null

  const title =
    streaming && phase === 'thinking' ? (
      <>
        <LoadingOutlined spin style={{ marginRight: 6 }} />
        深度思考中…
      </>
    ) : (
      <>
        <BulbOutlined />
        <span>深度思考</span>
      </>
    )

  return (
    <div className={`chat-msg-thinking${streaming && phase === 'thinking' ? ' chat-msg-thinking-live' : ''}`}>
      <button
        type="button"
        className="chat-msg-thinking-head"
        onClick={() => setOpen((v) => !v)}
      >
        {title}
        <span className="chat-msg-thinking-toggle">{open ? '收起' : '展开'}</span>
      </button>
      {open ? (
        <div
          className={`chat-msg-thinking-body${streaming && phase === 'thinking' ? ' chat-msg-thinking-body-streaming' : ''}`}
        >
          {content}
        </div>
      ) : null}
    </div>
  )
}

const ACTION_LABELS: Record<string, string> = {
  retry_expand_top_k: '未找到足够依据，扩大检索范围…',
  retry_next_es_page: '未找到足够依据，检索 ES 下一批分片…',
  retry_no_hits: '未命中切片，检索下一批…',
  answer_force_from_hits_stream: '已命中相关片段，强制依据知识库作答…',
  answer_stream: '已找到依据，生成回答',
  stop_not_found: '已达最大轮次，仍未找到',
  stop_not_found_stream: '已达最大轮次，仍未找到',
  stop_no_hits: '检索无结果',
  stop_es_unavailable: 'Elasticsearch 未连接，无法读取切片',
  final_stream_deep_think: '最后一轮：深度思考并回答',
}

function roundStatusLabel(r: HarnessRoundLog, live: boolean): string {
  if (r.status === 'searching') return live ? '正在检索知识库…' : '检索中'
  if (r.status === 'retrieved') {
    const n = r.hit_count ?? 0
    return live && n === 0 ? '检索完成，未命中切片' : `检索完成，命中 ${n} 条切片`
  }
  if (r.status === 'thinking') return live ? '深度思考中…' : '深度思考'
  if (r.status === 'judging') return live ? '判断是否找到答案…' : '判定中'
  if (r.found) return '已找到可回答依据'
  if (r.action) return ACTION_LABELS[r.action] || r.action
  return '本轮结束'
}

type ChatHarnessProgressProps = {
  rounds: HarnessRoundLog[]
  streaming?: boolean
  phase?: StreamPhase
}

export function ChatHarnessProgress({ rounds, streaming = false, phase = 'done' }: ChatHarnessProgressProps) {
  const [open, setOpen] = useState(true)
  const live = streaming && phase !== 'done' && phase !== 'answering'

  useEffect(() => {
    if (streaming && (phase === 'retrieval' || phase === 'judging')) {
      setOpen(true)
    }
  }, [streaming, phase, rounds.length])

  if (!rounds.length) return null

  return (
    <div className={`chat-msg-harness${live ? ' chat-msg-harness-live' : ''}`}>
      <button type="button" className="chat-msg-harness-head" onClick={() => setOpen((v) => !v)}>
        {live ? <LoadingOutlined spin style={{ marginRight: 6 }} /> : <SearchOutlined />}
        <span>检索过程</span>
        <span className="chat-msg-harness-toggle">{open ? '收起' : '展开'}</span>
      </button>
      {open ? (
        <ul className="chat-msg-harness-list">
          {rounds.map((r) => (
            <li key={r.round} className={`chat-msg-harness-item chat-msg-harness-item--${r.status}`}>
              <div className="chat-msg-harness-item-head">
                <span>
                  第 {r.round}/{r.max_rounds ?? '?'} 轮
                  {r.retrieval_top_k != null ? ` · top ${r.retrieval_top_k}` : ''}
                </span>
                <span className="chat-msg-harness-item-status">{roundStatusLabel(r, live && r.status !== 'done')}</span>
              </div>
              {r.titles && r.titles.length > 0 ? (
                <ul className="chat-msg-harness-titles">
                  {r.titles.map((t) => (
                    <li key={t}>{t}</li>
                  ))}
                </ul>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

export function ChatWebSources({ refs }: { refs: WebRef[] }) {
  if (!refs.length) return null
  return (
    <div className="chat-msg-web">
      <div className="chat-msg-web-title">
        <GlobalOutlined /> 联网参考（{refs.length}）
      </div>
      <ul className="chat-msg-web-list">
        {refs.map((r, i) => {
          const citeNum = r.cite_index ?? i + 1
          return (
          <li key={`${r.url || ''}-${i}`}>
            <span className="chat-msg-web-cite-index">{citeNum}.</span>
            {r.url ? (
              <a href={r.url} target="_blank" rel="noopener noreferrer" className="chat-msg-web-link">
                {r.title || r.url}
              </a>
            ) : (
              <span className="chat-msg-web-link">{r.title || '网页'}</span>
            )}
            {r.snippet ? <p className="chat-msg-web-snippet">{r.snippet}</p> : null}
          </li>
          )
        })}
      </ul>
    </div>
  )
}
